import type { ColumnDef } from '@tanstack/react-table'
import {
	ArrowLeft,
	Check,
	ChevronRight,
	Circle,
	CornerUpRight,
	X,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import type { RouterInputs, RouterOutputs } from 'shared/api'
import { trpcClient } from 'shared/api'
import { DataTable } from 'shared/components/data-table/data-table'
import { FacetedFilter } from 'shared/components/FacetedFilter'
import { PageFrame } from 'shared/components/page-frame'
import { ScanInput } from 'shared/components/scan-input'
import { SectionTitle } from 'shared/components/section'
import { ToneBanner, type Tone } from 'shared/components/tone-banner'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from 'shared/components/ui/alert-dialog'
import { Badge } from 'shared/components/ui/badge'
import { Button } from 'shared/components/ui/button'
import { useDataTable } from 'shared/hooks/use-data-table'
import { useTranslate } from 'shared/lib/i18n'
import { playTone } from 'shared/lib/tone-sound'
import { cn } from 'shared/lib/utils'

/**
 * «Мешки и коробки» — a Bag or a partner's whole Box at the Point: taking it from the
 * driver, opening it, shelving its Parcels (spec §13, §14, §26; ADR-0025, ADR-0037,
 * ADR-0047; tickets 28, 33, 57, 59). A Box taken in whole at the hub rides alone and
 * is worked here exactly as a Bag — the words name which it is (`kind`). The
 * operator scans its code, and the scan takes it — no seal, no weight (ADR-0047).
 * Then each Parcel is scanned onto the shelf; the first scan opens the Bag, there is
 * no opening step. A Parcel of another Point is answered yellow «чужая посылка —
 * пункт B, отложите»: it is not shelved and cannot be handed over here, and one the
 * Bag was not meant to hold is listed as an extra. «Завершить» turns whatever is
 * missing into a red shortage — asked first; who answers for it, support decides. The
 * list shows the finished Bags of the last 30 days when its stage filter asks for
 * them; a Parcel of a shortage scanned in its finished Bag goes onto the shelf and
 * lifts the shortage. The colour of every answer is the server's.
 *
 * The hub's look: the scan field on top and in focus, the answer under it
 * (`ToneBanner`, sounded in its tone, its stopwatch running), then the tables, one under the other. Scanners type like a keyboard
 * and press Enter. A Bag at work has its own address, `/point-bags/<containerId>`, so
 * the browser's «back» returns to the list.
 *
 * Every call is injected so the behaviour is tested without a backend; they
 * default to the real tRPC client.
 */

export type PointBagRow = RouterOutputs['pointBags']['Incoming'][number]
export type PointBagView = RouterOutputs['pointBags']['Bag']
export type PointBagAcceptRequest = RouterInputs['pointBags']['Accept']
export type PointBagAcceptAnswer = RouterOutputs['pointBags']['Accept']
export type PointBagShelveRequest = RouterInputs['pointBags']['Shelve']
export type PointBagShelveAnswer = RouterOutputs['pointBags']['Shelve']
export type PointBagFinishAnswer = RouterOutputs['pointBags']['Finish']
type RidingKind = PointBagRow['kind']
type HereStage = Extract<
	PointBagRow['stage'],
	'accepted' | 'opened' | 'finished'
>
const HERE_STAGES: HereStage[] = ['accepted', 'opened', 'finished']
/** The list opens on the Bags in work; the finished ones are a choice of the filter. */
const IN_WORK: string[] = ['accepted', 'opened']

/**
 * The key of a word that differs for a whole Box (its gender, its noun): `<key>Box`
 * for a partner's Box, `<key>` for a Bag.
 */
const forKind = <K extends string>(key: K, kind: RidingKind) =>
	(kind === 'partner_box' ? `${key}Box` : key) as K

export interface PointBagsProps {
	/** Test seam: the Bags for this Point; `finished` adds those finished lately. */
	list?: (input: { finished: boolean }) => Promise<PointBagRow[]>
	accept?: (input: PointBagAcceptRequest) => Promise<PointBagAcceptAnswer>
	loadBag?: (containerId: string) => Promise<PointBagView>
	shelve?: (input: PointBagShelveRequest) => Promise<PointBagShelveAnswer>
	finish?: (containerId: string) => Promise<PointBagFinishAnswer>
}

const listViaTrpc = (input: { finished: boolean }) =>
	trpcClient.pointBags.Incoming.query(input)
const acceptViaTrpc = (input: PointBagAcceptRequest) =>
	trpcClient.pointBags.Accept.mutate(input)
const loadBagViaTrpc = (containerId: string) =>
	trpcClient.pointBags.Bag.query({ containerId })
const shelveViaTrpc = (input: PointBagShelveRequest) =>
	trpcClient.pointBags.Shelve.mutate(input)
const finishViaTrpc = (containerId: string) =>
	trpcClient.pointBags.Finish.mutate({ containerId })

const failure = (error: unknown) =>
	error instanceof Error ? error.message : String(error)

/** A Bag still on its way to the Point (at the hub, with the driver); the rest are here. */
const onTheWay = (bag: PointBagRow) =>
	bag.stage === 'at_hub' || bag.stage === 'with_driver'

/** The same tables everywhere on this page: the whole list at once, no paging. */
function useWholeTable<T>(
	data: T[],
	columns: ColumnDef<T>[],
	getRowId: (row: T) => string,
) {
	return useDataTable({
		data,
		columns,
		rowCount: data.length,
		manualPagination: false,
		manualSorting: false,
		manualFiltering: false,
		enableRowSelection: false,
		getRowId,
		initialState: { pagination: { pageIndex: 0, pageSize: 5000 } },
	}).table
}

export function PointBags({
	list = listViaTrpc,
	accept = acceptViaTrpc,
	loadBag = loadBagViaTrpc,
	shelve = shelveViaTrpc,
	finish = finishViaTrpc,
}: PointBagsProps) {
	const { t } = useTranslate()
	const [bags, setBags] = useState<PointBagRow[] | null>(null)
	const [listError, setListError] = useState<string | null>(null)
	const navigate = useNavigate()
	const { containerId: working } = useParams()
	const [stages, setStages] = useState<string[] | undefined>(IN_WORK)
	// Nothing chosen is every stage, as everywhere a facet filter stands.
	const chosen = stages?.length ? stages : HERE_STAGES
	const withFinished = chosen.includes('finished')

	const refresh = useCallback(() => {
		list({ finished: withFinished }).then(
			(rows) => {
				setBags(rows)
				setListError(null)
			},
			(e) => setListError(failure(e)),
		)
	}, [list, withFinished])

	useEffect(() => {
		if (!working) refresh()
	}, [refresh, working])

	if (working) {
		return (
			<BagWork
				key={working}
				containerId={working}
				loadBag={loadBag}
				shelve={shelve}
				finish={finish}
				onDone={() => navigate('/point-bags')}
			/>
		)
	}

	return (
		<div data-testid='point-bags'>
			<PageFrame title='nav.pointBags'>
				<div className='flex flex-col gap-8'>
					<AcceptBag accept={accept} onAccepted={refresh} />
					{listError ? (
						<p
							className='text-sm text-destructive'
							data-testid='point-bags-error'
						>
							{t('pointBags.listFailed', { message: listError })}
						</p>
					) : bags === null ? (
						<p className='text-sm text-muted-foreground'>{t('hub.loading')}</p>
					) : (
						<div className='flex flex-col gap-8'>
							<BagsTable
								title={t('pointBags.hereTitle')}
								empty={t('pointBags.hereEmpty')}
								testId='point-bags-here'
								bags={bags.filter(
									(b) => !onTheWay(b) && chosen.includes(b.stage),
								)}
								onWork={(containerId) =>
									navigate(`/point-bags/${encodeURIComponent(containerId)}`)
								}
								aside={
									<FacetedFilter
										title={t('pointBags.colStage')}
										values={stages}
										setValues={setStages}
										options={HERE_STAGES.map((stage) => ({
											value: stage,
											label: t(`pointBagStage.${stage}`),
										}))}
									/>
								}
							/>
							<BagsTable
								title={t('pointBags.comingTitle')}
								empty={t('pointBags.comingEmpty')}
								testId='point-bags-coming'
								bags={bags.filter(onTheWay)}
							/>
						</div>
					)}
				</div>
			</PageFrame>
		</div>
	)
}

/** The Bags and whole Boxes of this Point: those here (opened by the row's button), or those on the way. */
function BagsTable({
	title,
	empty,
	testId,
	bags,
	onWork,
	aside,
}: {
	title: string
	empty: string
	testId: string
	bags: PointBagRow[]
	aside?: React.ReactNode
	/** Works the Bag — for those already taken from the driver. */
	onWork?: (containerId: string) => void
}) {
	const { t } = useTranslate()
	const columns = useMemo<ColumnDef<PointBagRow>[]>(
		() => [
			{
				id: 'code',
				header: t('pointBags.colBag'),
				cell: ({ row: { original: bag } }) => (
					<div className='flex items-baseline gap-2'>
						<span className='text-muted-foreground'>
							{t(`ridingKind.${bag.kind}`)}
						</span>
						<span className='font-mono font-semibold'>{bag.code}</span>
					</div>
				),
			},
			{
				id: 'parcels',
				header: t('pointBags.colParcels'),
				meta: {
					headClassName: 'text-right',
					cellClassName: 'text-right tabular-nums',
				},
				cell: ({ row }) => row.original.parcelCount,
			},
			...(onWork
				? []
				: [
						{
							id: 'driver',
							header: t('pointBags.colDriver'),
							cell: ({ row }) => row.original.trip?.driverName ?? '—',
						} satisfies ColumnDef<PointBagRow>,
					]),
			{
				id: 'stage',
				header: t('pointBags.colStage'),
				cell: ({ row: { original: bag } }) => (
					<Badge
						variant={
							bag.stage === 'finished'
								? bag.missing > 0
									? 'destructive'
									: 'secondary'
								: bag.stage === 'opened'
									? 'default'
									: bag.stage === 'accepted'
										? 'secondary'
										: 'outline'
						}
						// Done with: faded, as a disabled control is.
						className={cn(
							bag.stage === 'finished' && bag.missing === 0 && 'opacity-50',
						)}
						data-testid={`point-bag-stage-${bag.code}`}
					>
						{bag.stage === 'finished' && bag.missing > 0
							? t('pointBags.missingCount', { count: String(bag.missing) })
							: t(`pointBagStage.${bag.stage}`)}
					</Badge>
				),
			},
			...(onWork
				? [
						{
							id: 'actions',
							header: '',
							meta: { cellClassName: 'w-px text-right' },
							cell: ({ row: { original: bag } }) => (
								<Button
									size='sm'
									variant='ghost'
									className='underline-offset-4 hover:underline'
									data-testid={`point-bag-work-${bag.code}`}
									onClick={(event) => {
										event.stopPropagation()
										onWork(bag.containerId)
									}}
								>
									{t('pointBags.open')}
									<ChevronRight />
								</Button>
							),
						} satisfies ColumnDef<PointBagRow>,
					]
				: []),
		],
		[t, onWork],
	)
	const table = useWholeTable(bags, columns, (bag) => bag.containerId)
	return (
		<section className='flex min-w-0 flex-col gap-2' data-testid={testId}>
			<SectionTitle title={title} count={bags.length} aside={aside} />
			<DataTable
				table={table}
				withPagination={false}
				maxHeightClassName='max-h-[480px]'
				emptyText={empty}
				row={{
					attributes: (bag) => ({
						'data-testid': `point-bag-${bag.code}`,
						'data-stage': bag.stage,
						'data-kind': bag.kind,
					}),
				}}
			/>
		</section>
	)
}

/** Taking the Bag from the driver: the scan of its code is the act — no seal, no weight (ADR-0047). */
function AcceptBag({
	accept,
	onAccepted,
}: {
	accept: (input: PointBagAcceptRequest) => Promise<PointBagAcceptAnswer>
	onAccepted: () => void
}) {
	const { t } = useTranslate()
	const [sending, setSending] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [answer, setAnswer] = useState<PointBagAcceptAnswer | null>(null)
	const [shownAt, setShownAt] = useState(0)
	// Bumped after each answer, so the scan field remounts and takes the cursor back.
	const [scanRound, setScanRound] = useState(0)

	const take = async (code: string) => {
		if (sending) return
		setSending(true)
		setError(null)
		try {
			const res = await accept({ code })
			setAnswer(res)
			setShownAt(Date.now())
			playTone(res.tone)
			if (res.outcome !== 'refused') onAccepted()
		} catch (e) {
			setError(failure(e))
		} finally {
			setSending(false)
			setScanRound((n) => n + 1)
		}
	}

	return (
		<section className='flex flex-col gap-3'>
			<ScanInput
				key={scanRound}
				onScan={take}
				disabled={sending}
			/>
			{error && (
				<p
					role='alert'
					className='text-sm text-destructive'
					data-testid='point-bag-accept-error'
				>
					{t('pointBags.acceptFailed', { message: error })}
				</p>
			)}
			{answer && <AcceptBanner answer={answer} since={shownAt} />}
		</section>
	)
}

function AcceptBanner({
	answer,
	since,
}: {
	answer: PointBagAcceptAnswer
	since: number
}) {
	const { t } = useTranslate()
	const ids = {
		since,
		testId: 'point-bag-accept-answer',
		titleTestId: 'point-bag-accept-answer-title',
		detailTestId: 'point-bag-accept-answer-detail',
		attributes: { 'data-outcome': answer.outcome },
	}
	switch (answer.outcome) {
		case 'accepted':
			return (
				<ToneBanner
					tone='ready'
					{...ids}
					title={t(forKind('pointBags.acceptedTitle', answer.bag.kind), {
						code: answer.bag.code,
					})}
					detail={t(forKind('pointBags.acceptedDetail', answer.bag.kind), {
						count: String(answer.bag.parcelCount),
					})}
				/>
			)
		case 'already_accepted':
			return (
				<ToneBanner
					tone='attention'
					{...ids}
					title={t(forKind('pointBags.alreadyTitle', answer.bag.kind), {
						code: answer.bag.code,
					})}
					detail={t('pointBags.alreadyDetail')}
				/>
			)
		case 'refused':
			return (
				<ToneBanner
					tone='refused'
					{...ids}
					title={t('pointBags.refusedTitle')}
					detail={`${answer.code}: ${t(`pointBagAcceptRefusal.${answer.reason}`)}`}
				/>
			)
	}
}

/** One line of the Bag's table: a Parcel it was meant to hold, or an extra scanned at it. */
interface BagLine {
	parcelId: string
	trackingCode: string
	partnerName: string
	/** `missing` — still unscanned once the Bag is finished: the shortage. */
	status: 'waiting' | 'shelved' | 'set_aside' | 'missing'
	/** Another Point's — set aside for it. */
	otherPoint: string | null
	extra: boolean
}

/** One Bag at work: the scans onto the shelf — the first opens it —, the finish, the scans after it. */
function BagWork({
	containerId,
	loadBag,
	shelve,
	finish,
	onDone,
}: {
	containerId: string
	loadBag: (containerId: string) => Promise<PointBagView>
	shelve: (input: PointBagShelveRequest) => Promise<PointBagShelveAnswer>
	finish: (containerId: string) => Promise<PointBagFinishAnswer>
	onDone: () => void
}) {
	const { t } = useTranslate()
	const [view, setView] = useState<PointBagView | null>(null)
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [shelveAnswer, setShelveAnswer] = useState<PointBagShelveAnswer | null>(
		null,
	)
	// Set when a scan took a Parcel the finished Bag was short of: its shortage is lifted.
	const [lifted, setLifted] = useState(false)
	const [result, setResult] = useState<PointBagFinishAnswer | null>(null)
	// When the banner shown now came — its stopwatch counts from here.
	const [shownAt, setShownAt] = useState(0)
	const [asking, setAsking] = useState(false)
	// Bumped after every act, so the scan field remounts and takes the cursor back.
	const [scanRound, setScanRound] = useState(0)

	const reload = useCallback(
		() => loadBag(containerId).then(setView, (e) => setError(failure(e))),
		[loadBag, containerId],
	)
	useEffect(() => {
		reload()
	}, [reload])

	const guard = async (act: () => Promise<void>) => {
		if (busy) return
		setBusy(true)
		setError(null)
		try {
			await act()
		} catch (e) {
			setError(failure(e))
		} finally {
			setBusy(false)
			setScanRound((n) => n + 1)
		}
	}

	// Every banner sounds in its tone, and its stopwatch starts.
	const sound = (tone: Tone) => {
		setShownAt(Date.now())
		playTone(tone)
	}

	const doShelve = (code: string) =>
		guard(async () => {
			const answer = await shelve({ containerId, code })
			const wasShort =
				view?.finishedAt != null &&
				'parcelId' in answer &&
				view.parcels.some(
					(p) =>
						p.parcelId === answer.parcelId &&
						!p.shelved &&
						p.setAsideFor === null,
				)
			setLifted(
				wasShort &&
					(answer.outcome === 'shelved' || answer.outcome === 'set_aside'),
			)
			setResult(null)
			setShelveAnswer(answer)
			sound(answer.tone)
			await reload()
		})

	const doFinish = () =>
		guard(async () => {
			setShelveAnswer(null)
			const res = await finish(containerId)
			setResult(res)
			sound(res.tone)
			await reload()
		})

	const back = (
		<Button
			variant='ghost'
			size='sm'
			className='-ml-2.5 text-muted-foreground'
			data-testid='point-bag-back'
			onClick={onDone}
		>
			<ArrowLeft />
			{t('pointBags.back')}
		</Button>
	)

	if (!view) {
		return (
			<PageFrame title='nav.pointBags' back={back}>
				<p
					className='text-sm text-muted-foreground'
					data-testid='point-bag-loading'
				>
					{error
						? t('pointBags.listFailed', { message: error })
						: t('hub.loading')}
				</p>
			</PageFrame>
		)
	}

	const kind = view.kind
	const finished = result !== null || view.finishedAt !== null
	// What the Bag was meant to hold and nobody scanned: the shortage «Завершить» records,
	// and after it what may still be scanned out to lift it.
	const unscanned = view.parcels.filter(
		(p) => !p.shelved && p.setAsideFor === null,
	)
	const hint = !finished
		? t(forKind('pointBags.shelveHint', kind))
		: unscanned.length > 0
			? t('pointBags.lateScanHint')
			: null

	const lines: BagLine[] = [
		...view.parcels.map(
			(p): BagLine => ({
				parcelId: p.parcelId,
				trackingCode: p.trackingCode,
				partnerName: p.partnerName,
				status: p.shelved
					? 'shelved'
					: p.setAsideFor !== null
						? 'set_aside'
						: finished
							? 'missing'
							: 'waiting',
				otherPoint: p.setAsideFor,
				extra: false,
			}),
		),
		...view.extras.map(
			(x): BagLine => ({
				parcelId: x.parcelId,
				trackingCode: x.trackingCode,
				partnerName: x.partnerName,
				status: x.shelved ? 'shelved' : 'set_aside',
				otherPoint: x.shelved ? null : x.ownPointCode,
				extra: true,
			}),
		),
	]

	const onFinish = () => (unscanned.length > 0 ? setAsking(true) : doFinish())

	const actions = !finished ? (
		<Button
			type='button'
			// Finishing short records a shortage; only a later scan here lifts it.
			variant={unscanned.length > 0 ? 'destructive' : 'default'}
			data-testid='point-bag-finish'
			disabled={busy}
			onClick={onFinish}
		>
			{t('pointBags.finish')}
		</Button>
	) : undefined

	return (
		<div data-testid='point-bag-work'>
			<PageFrame
				back={back}
				heading={
					<>
						<span>
							{t(forKind('pointBags.workTitle', kind), { code: view.code })}
						</span>
						<Badge
							variant={
								finished
									? unscanned.length > 0
										? 'destructive'
										: 'secondary'
									: view.stage === 'opened'
										? 'default'
										: 'secondary'
							}
							data-testid='point-bag-stage'
						>
							{finished
								? unscanned.length > 0
									? t('pointBags.missingCount', {
											count: String(unscanned.length),
										})
									: t('pointBags.stageFinished')
								: t(`pointBagStage.${view.stage}`)}
						</Badge>
					</>
				}
				actions={actions}
			>
				<div className='flex flex-col gap-6'>
					<Counts view={view} />

					<section className='flex flex-col gap-3'>
						<ScanInput
							key={scanRound}
							onScan={doShelve}
							disabled={busy || asking}
						/>
						{error && (
							<p
								role='alert'
								className='text-sm text-destructive'
								data-testid='point-bag-error'
							>
								{t('pointBags.actFailed', { message: error })}
							</p>
						)}
						{result ? (
							<FinishBanner result={result} kind={kind} since={shownAt} />
						) : shelveAnswer ? (
							<ShelveBanner
								answer={shelveAnswer}
								kind={kind}
								lifted={lifted}
								since={shownAt}
							/>
						) : (
							hint && (
								<p
									className='text-xs text-muted-foreground'
									data-testid='point-bag-hint'
								>
									{hint}
								</p>
							)
						)}
					</section>

					<ParcelsTable lines={lines} />
				</div>

				<AlertDialog
					open={asking}
					onOpenChange={(next) => !next && setAsking(false)}
				>
					<AlertDialogContent
						data-testid='point-bag-finish-ask'
						onCloseAutoFocus={(event) => {
							// The cursor goes back to the scan field, not to «Завершить».
							event.preventDefault()
							setScanRound((n) => n + 1)
						}}
					>
						<AlertDialogHeader>
							<AlertDialogTitle>
								{t('pointBags.finishAskTitle', {
									count: String(unscanned.length),
								})}
							</AlertDialogTitle>
							<AlertDialogDescription>
								{t('pointBags.finishAskBody')}
							</AlertDialogDescription>
							<ul
								className='flex flex-col gap-0.5 text-sm'
								data-testid='point-bag-finish-ask-list'
							>
								{unscanned.slice(0, 10).map((p) => (
									<li key={p.parcelId} className='font-mono'>
										{p.trackingCode}
									</li>
								))}
								{unscanned.length > 10 && (
									<li className='text-muted-foreground'>
										{t('pointBags.finishAskMore', {
											count: String(unscanned.length - 10),
										})}
									</li>
								)}
							</ul>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel>
								{t('pointBags.finishCancel')}
							</AlertDialogCancel>
							<AlertDialogAction
								variant='destructive'
								data-testid='point-bag-finish-confirm'
								onClick={doFinish}
							>
								{t('pointBags.finishConfirm')}
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>
			</PageFrame>
		</div>
	)
}

/** «На полке N из M», a bar under it, and how many extras turned up. */
function Counts({ view }: { view: PointBagView }) {
	const { t } = useTranslate()
	const { shelved, expected } = view.counts
	return (
		<div className='flex min-w-60 flex-col gap-1.5 self-start'>
			<span
				className='text-lg leading-tight font-semibold tabular-nums'
				data-testid='point-bag-counts'
			>
				{t('pointBags.counts', {
					shelved: String(shelved),
					expected: String(expected),
				})}
				{view.extras.length > 0 && (
					<span className='font-normal text-amber-700 dark:text-amber-400'>
						{' · '}
						{t('pointBags.extrasCount', { count: String(view.extras.length) })}
					</span>
				)}
			</span>
			<div className='h-1.5 overflow-hidden rounded-full bg-muted'>
				<div
					className='h-full rounded-full bg-emerald-500 transition-all'
					style={{ width: `${expected ? (shelved * 100) / expected : 0}%` }}
				/>
			</div>
		</div>
	)
}

const MARK = {
	waiting: <Circle className='size-4 text-muted-foreground/60' aria-hidden />,
	shelved: <Check className='size-4 text-emerald-600' aria-hidden />,
	set_aside: <CornerUpRight className='size-4 text-amber-600' aria-hidden />,
	missing: <X className='size-4 text-destructive' aria-hidden />,
} as const

const AMBER_BADGE = 'border-amber-500/60 text-amber-700 dark:text-amber-400'

/** The Parcels of the Bag, then the extras scanned at it — each with where it is now. */
function ParcelsTable({ lines }: { lines: BagLine[] }) {
	const { t } = useTranslate()
	const columns = useMemo<ColumnDef<BagLine>[]>(
		() => [
			{
				id: 'mark',
				header: '',
				meta: { headClassName: 'w-8', cellClassName: 'w-8' },
				cell: ({ row }) => MARK[row.original.status],
			},
			{
				id: 'code',
				header: t('pointBags.colCode'),
				cell: ({ row: { original: line } }) => (
					<span
						className={cn(
							'font-mono',
							line.status === 'shelved' &&
								'text-emerald-700 dark:text-emerald-400',
							line.status === 'missing' && 'text-destructive',
						)}
					>
						{line.trackingCode}
					</span>
				),
			},
			{
				id: 'partner',
				header: t('pointBags.colPartner'),
				cell: ({ row }) => row.original.partnerName,
			},
			{
				id: 'status',
				header: t('pointBags.colStatus'),
				cell: ({ row: { original: line } }) => (
					<div className='flex flex-wrap items-center gap-1.5'>
						{line.status === 'shelved' ? (
							<Badge variant='secondary'>{t('pointBags.statusShelved')}</Badge>
						) : line.status === 'set_aside' ? (
							<Badge variant='outline' className={AMBER_BADGE}>
								{t('pointBags.statusSetAside', {
									point: line.otherPoint ?? '',
								})}
							</Badge>
						) : line.status === 'missing' ? (
							<Badge variant='destructive'>
								{t('pointBags.statusMissing')}
							</Badge>
						) : (
							<Badge variant='outline'>{t('pointBags.statusWaiting')}</Badge>
						)}
						{line.extra && (
							<Badge variant='outline' className={AMBER_BADGE}>
								{t('pointBags.statusExtra')}
							</Badge>
						)}
					</div>
				),
			},
		],
		[t],
	)
	const table = useWholeTable(lines, columns, (line) => line.parcelId)
	return (
		<section className='flex min-w-0 flex-col gap-2'>
			<SectionTitle title={t('pointBags.parcelsTitle')} count={lines.length} />
			<DataTable
				table={table}
				withPagination={false}
				emptyText={t('pointBags.parcelsEmpty')}
				row={{
					attributes: (line) =>
						line.extra
							? {
									'data-testid': `point-bag-extra-${line.trackingCode}`,
									'data-status': line.status,
								}
							: {
									'data-testid': `point-bag-parcel-${line.trackingCode}`,
									'data-status': line.status,
									'data-shelved': line.status === 'shelved',
									'data-set-aside': line.status === 'set_aside',
								},
				}}
			/>
		</section>
	)
}

function ShelveBanner({
	answer,
	kind,
	lifted,
	since,
}: {
	answer: PointBagShelveAnswer
	kind: RidingKind
	/** The scan took a Parcel the finished Bag was short of. */
	lifted: boolean
	since: number
}) {
	const { t } = useTranslate()
	const ids = {
		since,
		testId: 'point-bag-shelve-answer',
		titleTestId: 'point-bag-shelve-answer-title',
		detailTestId: 'point-bag-shelve-answer-detail',
		attributes: { 'data-outcome': answer.outcome },
	}
	const progress = t('pointBags.counts', {
		shelved: String(answer.counts.shelved),
		expected: String(answer.counts.expected),
	})
	switch (answer.outcome) {
		case 'shelved':
			return (
				<ToneBanner
					tone='ready'
					{...ids}
					title={t(
						lifted ? 'pointBags.shortageLiftedTitle' : 'pointBags.shelvedTitle',
						{ code: answer.trackingCode },
					)}
					detail={progress}
				/>
			)
		case 'set_aside':
			return (
				<ToneBanner
					tone='attention'
					{...ids}
					title={t('pointBags.setAsideTitle', { point: answer.ownPointCode })}
					detail={[
						answer.trackingCode,
						t('pointBags.setAsideDetail'),
						answer.extra ? t(forKind('pointBags.setAsideExtra', kind)) : '',
					]
						.filter(Boolean)
						.join(' · ')}
				/>
			)
		case 'shelved_extra':
			return (
				<ToneBanner
					tone='attention'
					{...ids}
					title={t('pointBags.shelvedExtraTitle', {
						code: answer.trackingCode,
					})}
					detail={[
						t(forKind('pointBags.shelvedExtraDetail', kind)),
						progress,
					].join(' · ')}
				/>
			)
		case 'already_set_aside':
			return (
				<ToneBanner
					tone='attention'
					{...ids}
					title={t('pointBags.alreadySetAsideTitle', {
						code: answer.trackingCode,
					})}
					detail={t('pointBags.setAsideMark', { point: answer.ownPointCode })}
				/>
			)
		case 'already_shelved':
			return (
				<ToneBanner
					tone='attention'
					{...ids}
					title={t('pointBags.alreadyShelvedTitle', {
						code: answer.trackingCode,
					})}
					detail={progress}
				/>
			)
		case 'found':
			// A Parcel declared lost, found by this scan (ticket 43): on this shelf, or set aside.
			return (
				<ToneBanner
					tone='attention'
					{...ids}
					title={
						answer.where === 'own_point'
							? t('pointBags.foundTitle', { code: answer.trackingCode })
							: t('pointBags.foundOtherTitle', { point: answer.ownPointCode })
					}
					detail={[answer.trackingCode, t('pointBags.foundDetail')].join(' · ')}
				/>
			)
		case 'refused':
			return (
				<ToneBanner
					tone='refused'
					{...ids}
					title={t('pointBags.shelveRefusedTitle')}
					detail={`${answer.code}: ${
						answer.reason === 'sealed_elsewhere' && answer.sealedIn
							? t(
									answer.otherPoint
										? 'pointBags.sealedElsewhereOther'
										: 'pointBags.sealedElsewhereIn',
									{
										bag: answer.sealedIn,
									},
								)
							: t(`pointBagShelveRefusal.${answer.reason}`)
					}`}
				/>
			)
	}
}

/**
 * Green when everything matched; yellow with extras only; red with the missing
 * Parcels and who answers. The extras — another Point's, set aside, or this
 * Point's, put on the shelf — are named either way.
 */
function FinishBanner({
	result,
	kind,
	since,
}: {
	result: PointBagFinishAnswer
	kind: RidingKind
	since: number
}) {
	const { t } = useTranslate()
	const ids = {
		since,
		testId: 'point-bag-finish-answer',
		titleTestId: 'point-bag-finish-answer-title',
		detailTestId: 'point-bag-finish-answer-detail',
	}
	const extras = result.extras.length > 0 && (
		<div className='text-sm font-medium' data-testid='point-bag-finish-extras'>
			{t('pointBags.extrasCount', { count: String(result.extras.length) })}:{' '}
			{result.extras
				.map(
					(x) =>
						`${x.trackingCode} — ${
							x.shelved
								? t('pointBags.extraShelvedMark')
								: t('pointBags.setAsideMark', { point: x.ownPointCode })
						}`,
				)
				.join('; ')}
		</div>
	)
	if (result.tone === 'ready' || result.tone === 'attention') {
		return (
			<ToneBanner
				tone={result.tone}
				{...ids}
				title={t(forKind('pointBags.finishedTitle', kind))}
				detail={t('pointBags.finishedDetail', {
					count: String(result.counts.shelved),
				})}
			>
				{extras}
			</ToneBanner>
		)
	}
	return (
		<ToneBanner
			tone='refused'
			{...ids}
			title={t('pointBags.shortageTitle', {
				count: String(result.shortage.length),
			})}
			detail={t('pointBags.shortageSupportDecides')}
		>
			<div
				className='font-mono text-sm font-medium'
				data-testid='point-bag-shortage'
			>
				{result.shortage.map((s) => s.trackingCode).join(', ')}
			</div>
			{extras}
		</ToneBanner>
	)
}
